// PriorityConfig.tsx
import React, { useState, useEffect } from 'react';
import { useConfigStore } from '@/store/configStore';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/common/Input';
import { Edit2, Trash2 } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { Button } from '@/components/common/Button';

export const PriorityConfig: React.FC<{ onSetActionCallback?: (callbacks: { add?: () => void }) => void }> = ({ onSetActionCallback }) => {
  const { priorities, loading, fetchPriorities, createPriority, updatePriority, deletePriority } = useConfigStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPriority, setEditingPriority] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', color: '#3b82f6' });
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);

  useEffect(() => {
    fetchPriorities();
  }, []);

  useEffect(() => {
    onSetActionCallback?.({
      add: () => setShowAddModal(true)
    });
  }, [onSetActionCallback]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedPriorities(priorities.map(priority => priority._id));
    } else {
      setSelectedPriorities([]);
    }
  };

  const handleSelectPriority = (priorityId: string) => {
    setSelectedPriorities(prev => 
      prev.includes(priorityId) 
        ? prev.filter(id => id !== priorityId)
        : [...prev, priorityId]
    );
  };

  const handleSubmit = async () => {
    try {
      if (editingPriority) {
        await updatePriority(editingPriority._id, formData);
      } else {
        await createPriority(formData);
      }
      handleClose();
    } catch (error) {
      // Error handled in store
    }
  };

  const handleClose = () => {
    setShowAddModal(false);
    setEditingPriority(null);
    setFormData({ name: '', color: '#3b82f6' });
  };

  const handleEdit = (priority: any) => {
    setEditingPriority(priority);
    setFormData({ name: priority.name, color: priority.color });
    setShowAddModal(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}" priority?`)) {
      deletePriority(id);
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="h-full bg-white mx-0 my-0">
      <div className="h-full flex flex-col">
        <div className="overflow-auto flex-1">
          <table className="w-full border border-gray-200 border-collapse">
            <thead>
              <tr className="border border-gray-200">
                <th className="text-left bg-gray-100 px-4 border py-1 w-10">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300"
                    onChange={handleSelectAll}
                    checked={selectedPriorities.length === priorities.length && priorities.length > 0}
                  />
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  PRIORITY
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  ADDED ON
                </th>
                <th className="w-20 bg-gray-100 border"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-500 border">
                    Loading priorities...
                  </td>
                </tr>
              ) : priorities.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-500 border">
                    No priorities found
                  </td>
                </tr>
              ) : (
                priorities.map((priority) => (
                  <tr key={priority._id} className="border border-gray-100 hover:bg-gray-50">
                    <td className="px-4 border py-1">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300"
                        checked={selectedPriorities.includes(priority._id)}
                        onChange={() => handleSelectPriority(priority._id)}
                      />
                    </td>
                    <td className="px-4 border py-1">
                      <div className="flex items-center">
                        <div
                          className="w-4 h-4 rounded-full mr-3"
                          style={{ backgroundColor: priority.color }}
                        />
                        <span className="text-sm font-medium">{priority.name}</span>
                      </div>
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600">
                      {formatDate(priority.createdAt)}
                    </td>
                    <td className="px-4 border py-1">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          onClick={() => handleEdit(priority)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          onClick={() => handleDelete(priority._id, priority.name)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 border py-3 border-t border-gray-200 bg-gray-50 flex items-center">
          <div className="text-sm text-gray-600 uppercase">
            COUNT <span className="ml-4 font-medium">{priorities.length}</span>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={handleClose}
        title={editingPriority ? 'Edit Priority' : 'Add Priority'}
      >
        <div className="space-y-4">
          <Input
            label="Priority Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter priority name"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <HexColorPicker
              color={formData.color}
              onChange={(color) => setFormData({ ...formData, color })}
              style={{ width: '100%' }}
            />
            <div className="mt-2 flex items-center space-x-2">
              <div
                className="w-10 h-10 rounded border border-gray-300"
                style={{ backgroundColor: formData.color }}
              />
              <span className="text-sm text-gray-600">{formData.color}</span>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!formData.name}
            >
              {editingPriority ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};